import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Branding je Mandant (PowerCare, 2026-09-05). Laedt /api/branding (oeffentlich, auch vor dem Login)
 * und setzt daraus die CSS-Variablen --primary-50…900 (Tailwind `primary-*`, `ps-orange-*`),
 * den Tab-Titel und die theme-color. Logo: <Logo/> zeigt das hochgeladene Bild des Mandanten,
 * sonst die Wortmarke (Kurzname in der Primaerfarbe).
 */
export type Branding = {
  slug: string | null; name: string; kurzname: string; untertitel: string;
  farbe: string; farbe_dunkel: string; fusszeile: string; logo_url: string | null; favicon_url?: string | null;
  funktionen?: { simed: boolean; hl7: boolean };
};

const STANDARD: Branding = {
  slug: null, name: 'PowerCare', kurzname: 'PowerCare', untertitel: 'KI-Assistenz für die Praxis',
  farbe: '#EA580C', farbe_dunkel: '#0F172A', fusszeile: '', logo_url: null,
};

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function mix(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * t)) as [number, number, number];
}
/** Farbskala 50–900 aus EINER Grundfarbe: helle Stufen mit Weiss, dunkle mit Schwarz gemischt. */
export function skala(hex: string): Record<number, [number, number, number]> {
  const g = hexToRgb(hex);
  const weiss: [number, number, number] = [255, 255, 255], schwarz: [number, number, number] = [15, 10, 8];
  return {
    50: mix(g, weiss, 0.93), 100: mix(g, weiss, 0.85), 200: mix(g, weiss, 0.70), 300: mix(g, weiss, 0.50), 400: mix(g, weiss, 0.26),
    500: g, 600: mix(g, schwarz, 0.14), 700: mix(g, schwarz, 0.30), 800: mix(g, schwarz, 0.45), 900: mix(g, schwarz, 0.60),
  };
}

export function anwenden(b: Branding) {
  const root = document.documentElement;
  const s = skala(/^#[0-9a-fA-F]{6}$/.test(b.farbe) ? b.farbe : STANDARD.farbe);
  Object.entries(s).forEach(([k, v]) => root.style.setProperty(`--primary-${k}`, v.join(' ')));
  document.title = b.kurzname || 'PowerCare';
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', b.farbe);
  // Favicon je Mandant (Betreiber-Konsole); ohne eigenes bleibt das PowerCare-Icon aus public/favicon
  // (ohne eigenes bleiben die Links aus index.html: favicon.ico, favicon.svg, apple-touch-icon.png)
  if (b.favicon_url) {
    document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((l) => {
      l.href = b.favicon_url!; l.removeAttribute('type'); l.removeAttribute('sizes');
    });
  }
}

const Ctx = createContext<{ branding: Branding; reload: () => Promise<void> }>({ branding: STANDARD, reload: async () => {} });
export const useBranding = () => useContext(Ctx);

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<Branding>(STANDARD);
  const reload = useCallback(async () => {
    try {
      const r = await fetch('/api/branding', { credentials: 'include' });
      if (r.ok) { const b = { ...STANDARD, ...(await r.json()) } as Branding; setBranding(b); anwenden(b); }
    } catch { /* Standard bleibt */ }
  }, []);
  useEffect(() => { anwenden(STANDARD); void reload(); }, [reload]);
  return <Ctx.Provider value={{ branding, reload }}>{children}</Ctx.Provider>;
}

/** Logo des Mandanten oder PowerCare-Symbol + Wortmarke. */
export function Logo({ className = 'h-12', textClass = 'text-2xl', farbe = 'text-primary-600' }:
  { className?: string; textClass?: string; farbe?: string }) {
  const { branding } = useBranding();
  if (branding.logo_url) return <img src={branding.logo_url} alt={branding.name} className={`${className} max-w-full object-contain`} />;
  // Ohne eigenes Logo: PowerCare-Symbol (Power-Apps-Familie) vor der Wortmarke des Mandanten
  return (
    <span className={`${textClass} flex min-w-0 items-center gap-[0.35em] font-bold tracking-tight ${farbe}`}>
      <img src="/icons/icon.svg" alt="" aria-hidden="true" className="h-[1.5em] w-[1.5em] shrink-0" />
      <span className="truncate">{branding.kurzname}</span>
    </span>
  );
}
